import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { TrendingUp, TrendingDown, Euro, Loader2, ClipboardCheck, HardHat, Wallet, X, FileSpreadsheet, FileText, Wrench } from "lucide-react";
import { toast } from "sonner";
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid,
  PieChart, Pie, Cell,
} from "recharts";

const fmt = (n) => (n || 0).toLocaleString("it-IT", { style: "currency", currency: "EUR" });
const MESI = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
const shortMonth = (iso) => MESI[parseInt(iso.split("-")[1], 10) - 1] || iso;
const PIE_COLORS = ["#2C5530", "#E07A5F", "#3D5A80", "#F2CC8F", "#81B29A", "#9A5B88", "#C97B4A", "#5F797B", "#B23A48", "#6B705C"];

function PieCard({ title, data, testid, numeric = false }) {
  const rows = (data || []).filter((d) => d.value > 0);
  return (
    <div className="card-shell p-4" data-testid={testid}>
      <h3 className="font-bold text-sm mb-2" style={{ fontFamily: "Manrope" }}>{title}</h3>
      {rows.length === 0 ? (
        <div className="h-52 flex items-center justify-center text-xs" style={{ color: "var(--text-muted)" }}>
          Nessun dato nel periodo
        </div>
      ) : (
        <div style={{ width: "100%", height: 230 }}>
          <ResponsiveContainer>
            <PieChart>
              <Pie data={rows} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={40} outerRadius={70}
                paddingAngle={2} label={({ percent }) => `${(percent * 100).toFixed(0)}%`}>
                {rows.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={(v, n) => [numeric ? `${v} client${v === 1 ? "e" : "i"}` : fmt(v), n]} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}

function Kpi({ icon: Icon, label, value, tone, testid }) {
  const color = tone === "red" ? "#B30000" : tone === "green" ? "#1B5E20" : "var(--primary)";
  return (
    <div className="card-shell p-5 flex gap-4 items-center" data-testid={testid}>
      <div className="w-12 h-12 rounded-lg flex items-center justify-center" style={{ background: `${color}15`, color }}>
        <Icon className="w-6 h-6" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs uppercase font-semibold tracking-wider" style={{ color: "var(--text-muted)" }}>{label}</div>
        <div className="text-xl font-extrabold" style={{ fontFamily: "Manrope", color }}>{value}</div>
      </div>
    </div>
  );
}

export default function Analytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const handleExport = async (kind) => {
    setExporting(kind);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      const res = await api.get(`/analytics/export/${kind}?${params.toString()}`, { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const match = cd.match(/filename="?([^";]+)"?/);
      const filename = match ? match[1] : `analytics.${kind === "excel" ? "xlsx" : "pdf"}`;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(kind === "excel" ? "Excel scaricato" : "PDF scaricato");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setExporting(""); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      const { data } = await api.get(`/analytics/overview?${params.toString()}`);
      setData(data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [dateFrom, dateTo]);

  const setThisMonth = () => {
    const now = new Date();
    const y = now.getFullYear(), m = String(now.getMonth() + 1).padStart(2, "0");
    const last = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    setDateFrom(`${y}-${m}-01`); setDateTo(`${y}-${m}-${String(last).padStart(2, "0")}`);
  };
  const setThisYear = () => {
    const y = new Date().getFullYear();
    setDateFrom(`${y}-01-01`); setDateTo(`${y}-12-31`);
  };

  if (loading && !data) return <div className="flex items-center gap-2 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Caricamento…</div>;
  if (!data) return null;

  const t = data.totals;
  const inputStyle = { borderColor: "var(--border)", background: "#fff" };
  const chartData = data.trend_mensile.map((r) => ({ ...r, name: shortMonth(r.month) }));
  const pieBy = (key) => data.rows.map((r) => ({ name: r.commessa_name, value: r[key] || 0 }));
  const pieExtra = data.rows.map((r) => ({ name: r.commessa_name, value: (r.guadagno_extra || 0) + (r.guadagno_ple || 0) + (r.guadagno_fermo || 0) }));
  const pieBonus = (data.bonus_dipendenti || []).map((b) => ({ name: b.nome, value: b.importo }));
  const pieServizi = (data.servizi_extra || []).map((s) => ({ name: s.voce, value: s.importo }));

  return (
    <div className="space-y-6" data-testid="analytics-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight" style={{ fontFamily: "Manrope" }}>Analytics</h1>
          <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>
            Fatturato, costi e margini per commessa · dati riservati
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={setThisMonth} className="btn-secondary text-xs" data-testid="filter-this-month">Questo mese</button>
          <button onClick={setThisYear} className="btn-secondary text-xs" data-testid="filter-this-year">Quest'anno</button>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
            className="px-2 py-1.5 text-sm rounded-md border outline-none" style={inputStyle} data-testid="filter-date-from" />
          <span className="text-xs" style={{ color: "var(--text-muted)" }}>→</span>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
            className="px-2 py-1.5 text-sm rounded-md border outline-none" style={inputStyle} data-testid="filter-date-to" />
          {(dateFrom || dateTo) && (
            <button onClick={() => { setDateFrom(""); setDateTo(""); }} className="p-1.5 rounded hover:bg-black/5" title="Rimuovi filtro" data-testid="filter-clear">
              <X className="w-4 h-4" />
            </button>
          )}
          <button onClick={() => handleExport("excel")} disabled={!!exporting} className="btn-secondary text-xs flex items-center gap-1.5" data-testid="export-excel-btn">
            {exporting === "excel" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileSpreadsheet className="w-3.5 h-3.5" />} Excel
          </button>
          <button onClick={() => handleExport("pdf")} disabled={!!exporting} className="btn-secondary text-xs flex items-center gap-1.5" data-testid="export-pdf-btn">
            {exporting === "pdf" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />} PDF
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Kpi icon={Euro} label="Fatturato totale" value={fmt(t.guadagno_totale)} testid="kpi-guadagno" />
        <Kpi icon={Wrench} label="Interventi €" value={fmt(t.guadagno_interventi)} testid="kpi-interventi" />
        <Kpi icon={Wallet} label="Costi totali" value={fmt(t.costi_totale)} tone="red" testid="kpi-costi" />
        <Kpi icon={t.margine >= 0 ? TrendingUp : TrendingDown} label="Margine" value={fmt(t.margine)} tone={t.margine >= 0 ? "green" : "red"} testid="kpi-margine" />
        <Kpi icon={ClipboardCheck} label="Sopralluoghi completati" value={t.sopralluoghi_completati} testid="kpi-sopralluoghi" />
        <Kpi icon={HardHat} label="Installazioni completate" value={t.installazioni_completate} testid="kpi-installazioni" />
      </div>

      {/* Spaccato costi */}
      <div className="card-shell p-5" data-testid="costi-breakdown">
        <h2 className="font-bold text-lg mb-3" style={{ fontFamily: "Manrope" }}>Spaccato costi</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div className="p-3 rounded-md" style={{ background: "var(--surface-muted)" }}>
            <div className="text-xs uppercase font-semibold tracking-wider" style={{ color: "var(--text-muted)" }}>Bonus dipendenti</div>
            <div className="text-lg font-bold mt-0.5" data-testid="costo-bonus">{fmt(t.costo_bonus)}</div>
          </div>
          <div className="p-3 rounded-md" style={{ background: "var(--surface-muted)" }}>
            <div className="text-xs uppercase font-semibold tracking-wider" style={{ color: "var(--text-muted)" }}>Materiali usati</div>
            <div className="text-lg font-bold mt-0.5" data-testid="costo-materiali">{fmt(t.costo_materiali)}</div>
          </div>
          <div className="p-3 rounded-md" style={{ background: "var(--surface-muted)" }}>
            <div className="text-xs uppercase font-semibold tracking-wider" style={{ color: "var(--text-muted)" }}>Trasferte (vitto/alloggio/carburante)</div>
            <div className="text-lg font-bold mt-0.5" data-testid="costo-trasferta">{fmt(t.costo_trasferta)}</div>
          </div>
        </div>
      </div>

      {/* Servizi extra: Wallbox / GSE / PDC */}
      <div className="card-shell p-5" data-testid="servizi-extra-card">
        <h2 className="font-bold text-lg mb-3" style={{ fontFamily: "Manrope" }}>Servizi extra</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          {(data.servizi_extra || []).map((s) => (
            <div key={s.key} className="p-3 rounded-md" style={{ background: "var(--surface-muted)" }} data-testid={`servizio-${s.key}`}>
              <div className="text-xs uppercase font-semibold tracking-wider" style={{ color: "var(--text-muted)" }}>{s.voce}</div>
              <div className="flex items-baseline justify-between mt-0.5">
                <span className="text-lg font-bold" data-testid={`servizio-${s.key}-importo`}>{fmt(s.importo)}</span>
                <span className="text-xs font-semibold px-1.5 py-0.5 rounded-full" style={{ background: "#E6F4EA", color: "#1B5E20" }} data-testid={`servizio-${s.key}-count`}>
                  {s.count} attivazion{s.count === 1 ? "e" : "i"}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Tabella per commessa */}
      <div className="card-shell p-5" data-testid="analytics-commesse-table">
        <h2 className="font-bold text-lg mb-3" style={{ fontFamily: "Manrope" }}>Spaccato per commessa</h2>
        {data.rows.length === 0 ? (
          <div className="text-sm p-4 rounded-md text-center" style={{ background: "var(--surface-muted)", color: "var(--text-muted)" }}>
            Nessun dato nel periodo selezionato
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>
                  <th className="py-2 pr-2">Commessa</th>
                  <th className="py-2 px-2 text-center">Clienti</th>
                  <th className="py-2 px-2 text-center">Sopralluoghi ✓</th>
                  <th className="py-2 px-2 text-center">Installazioni ✓</th>
                  <th className="py-2 px-2 text-right">Sopralluogo €</th>
                  <th className="py-2 px-2 text-right">Installazione €</th>
                  <th className="py-2 px-2 text-right">Extra €</th>
                  <th className="py-2 px-2 text-right">PLE €</th>
                  <th className="py-2 px-2 text-right">Fermo Dep. €</th>
                  <th className="py-2 px-2 text-right">Interventi €</th>
                  <th className="py-2 px-2 text-right">Fatturato</th>
                  <th className="py-2 px-2 text-right">Costi</th>
                  <th className="py-2 pl-2 text-right">Margine</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r) => (
                  <tr key={r.commessa_id} className="border-t" style={{ borderColor: "var(--border)" }} data-testid={`analytics-row-${r.commessa_id}`}>
                    <td className="py-2 pr-2 font-medium">{r.commessa_name}</td>
                    <td className="py-2 px-2 text-center">{r.clienti}</td>
                    <td className="py-2 px-2 text-center">{r.sopralluoghi_completati}</td>
                    <td className="py-2 px-2 text-center">{r.installazioni_completate}</td>
                    <td className="py-2 px-2 text-right">{fmt(r.guadagno_sopralluogo)}</td>
                    <td className="py-2 px-2 text-right">{fmt(r.guadagno_installazione)}</td>
                    <td className="py-2 px-2 text-right">{fmt(r.guadagno_extra)}</td>
                    <td className="py-2 px-2 text-right">{fmt(r.guadagno_ple)}</td>
                    <td className="py-2 px-2 text-right">{fmt(r.guadagno_fermo)}</td>
                    <td className="py-2 px-2 text-right">{fmt(r.guadagno_interventi)}</td>
                    <td className="py-2 px-2 text-right font-bold" style={{ color: "var(--primary)" }}>{fmt(r.guadagno_totale)}</td>
                    <td className="py-2 px-2 text-right" style={{ color: "#B30000" }} title={`Bonus ${fmt(r.costo_bonus)} · Materiali ${fmt(r.costo_materiali)} · Trasferte ${fmt(r.costo_trasferta)}`}>{fmt(r.costi_totale)}</td>
                    <td className="py-2 pl-2 text-right font-extrabold" style={{ color: r.margine >= 0 ? "#1B5E20" : "#B30000" }}>{fmt(r.margine)}</td>
                  </tr>
                ))}
                <tr className="border-t-2 font-bold" style={{ borderColor: "var(--border)" }} data-testid="analytics-totals">
                  <td className="py-2 pr-2">Totale</td>
                  <td className="py-2 px-2 text-center">{t.clienti}</td>
                  <td className="py-2 px-2 text-center">{t.sopralluoghi_completati}</td>
                  <td className="py-2 px-2 text-center">{t.installazioni_completate}</td>
                  <td className="py-2 px-2 text-right">{fmt(t.guadagno_sopralluogo)}</td>
                  <td className="py-2 px-2 text-right">{fmt(t.guadagno_installazione)}</td>
                  <td className="py-2 px-2 text-right">{fmt(t.guadagno_extra)}</td>
                  <td className="py-2 px-2 text-right">{fmt(t.guadagno_ple)}</td>
                  <td className="py-2 px-2 text-right">{fmt(t.guadagno_fermo)}</td>
                  <td className="py-2 px-2 text-right">{fmt(t.guadagno_interventi)}</td>
                  <td className="py-2 px-2 text-right" style={{ color: "var(--primary)" }}>{fmt(t.guadagno_totale)}</td>
                  <td className="py-2 px-2 text-right" style={{ color: "#B30000" }}>{fmt(t.costi_totale)}</td>
                  <td className="py-2 pl-2 text-right" style={{ color: t.margine >= 0 ? "#1B5E20" : "#B30000" }}>{fmt(t.margine)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Grafici a torta */}
      <div>
        <h2 className="font-bold text-lg mb-3" style={{ fontFamily: "Manrope" }}>Suddivisioni</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          <PieCard title="Fatturato per commessa" data={pieBy("guadagno_totale")} testid="pie-fatturato" />
          <PieCard title="Sopralluoghi completati per commessa (n° clienti)" data={pieBy("sopralluoghi_completati")} numeric testid="pie-sopralluoghi" />
          <PieCard title="Installazioni completate per commessa (n° clienti)" data={pieBy("installazioni_completate")} numeric testid="pie-installazioni" />
          <PieCard title="Extra € per commessa (kW + prodotti + wallbox + GSE + PDC + PLE + fermo)" data={pieExtra} testid="pie-extra" />
          <PieCard title="Bonus per operaio" data={pieBonus} testid="pie-bonus" />
          <PieCard title="Servizi extra (Wallbox / GSE / PDC)" data={pieServizi} testid="pie-servizi" />
        </div>
      </div>

      {/* Trend mensile */}
      <div className="card-shell p-5" data-testid="analytics-trend-chart">
        <h2 className="font-bold text-lg mb-1" style={{ fontFamily: "Manrope" }}>Andamento ultimi 12 mesi</h2>
        <p className="text-xs mb-4" style={{ color: "var(--text-muted)" }}>Fatturato vs costi con linea del margine (non filtrato dal periodo)</p>
        <div style={{ width: "100%", height: 320 }}>
          <ResponsiveContainer>
            <ComposedChart data={chartData} margin={{ top: 8, right: 16, left: 8, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `€${v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}`} />
              <Tooltip formatter={(v, n) => [fmt(v), n]} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="guadagno" name="Fatturato" fill="#2C5530" radius={[3, 3, 0, 0]} />
              <Bar dataKey="costi" name="Costi" fill="#E07A5F" radius={[3, 3, 0, 0]} />
              <Line type="monotone" dataKey="margine" name="Margine" stroke="#1B5E20" strokeWidth={2} dot={{ r: 3 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
